import { api, desenvolver } from '../api';
import type { TenantResumen } from '../api';

/**
 * Los tenants entre los que se elige a nombre de quién factura algo.
 *
 * Una sola llamada, y a propósito: esto es **`/api/v1/consultas/tenants`**, la
 * superficie de solo lectura. El ABM de tenants vive en `/api/v1/admin/tenants`
 * y no se toca desde acá.
 *
 * # POR QUÉ ES UN MÓDULO APARTE Y NO PARTE DE `sistemas/`
 *
 * Lo estrena la lista blanca de KAN-144, pero no es de los sistemas: el selector
 * de la pantalla de emisión (KAN-145) necesita exactamente esta lista, y ahí la
 * va a pedir un `OPERADOR`, que no puede ver `/admin/**`. Dejarlo dentro de
 * `sistemas/` obligaría a que una pantalla de emisión importara del ABM de
 * administración para leer algo que no es suyo.
 *
 * ⚠️ **Al leer, un sistema conectado ve solo su lista blanca.** No cambia nada
 * para el front —acá siempre pregunta una persona con su JWT— pero explica por
 * qué el backend no expone esto sin credencial.
 */

/**
 * `GET /api/v1/consultas/tenants`. Ordenado por razón social.
 *
 * **`soloEmisores` por defecto en `false`**, que es lo que corresponde para la
 * lista blanca de un sistema: autorizar a una app a facturar para un cliente
 * cuya delegación todavía está en trámite es normal —el permiso se configura
 * antes de que ARCA conteste—, y filtrarlos acá haría desaparecer del selector
 * justo a los tenants recién dados de alta. El selector de emisión (KAN-145)
 * pasa `soloEmisores: true`: ahí ofrecer un tenant que va a fallar con `409` es
 * hacerle perder el tiempo a quien factura.
 */
export function listarTenants(opciones?: { soloEmisores?: boolean }): Promise<TenantResumen[]> {
  return desenvolver(
    api.GET('/api/v1/consultas/tenants', {
      params: { query: { soloEmisores: opciones?.soloEmisores } },
    }),
  );
}

/**
 * Cómo se nombra un tenant en la pantalla: razón social **y** CUIT.
 *
 * Las dos cosas, no una. La razón social sola se puede repetir o parecerse
 * entre clientes ("Distribuidora del Sur SA" y "Distribuidora del Sur SRL"), y
 * acá elegir mal significa habilitar a una app a facturar a nombre del CUIT
 * equivocado. El CUIT desempata y es el dato que la persona puede verificar
 * contra un papel.
 */
export function nombreDeTenant(tenant: TenantResumen): string {
  return `${tenant.razonSocial} (CUIT ${tenant.cuit})`;
}
